/**
 * leadStatus — the commercial qualification of a prospect (new / contacted /
 * qualified / lost), NOT to be confused with two other notions that also
 * get called "status" in conversation on a ProspectQueue:
 *
 *   - `status` (prospectStatus.ts)         : the prospect's place in the
 *                                             CALL QUEUE (active / do_not_call
 *                                             / placeholder).
 *   - `last_call_status` / `status_override` (callOutcomeLabels.ts) : the
 *                                             OUTCOME of the last call, or a
 *                                             manual override of it.
 *   - `lead_status` (here)                 : the COMMERCIAL QUALIFICATION
 *                                             set by hand from the prospect
 *                                             card, or received from a
 *                                             connected CRM.
 *
 * Mirror backend: server/src/domain/crm/entities/ProspectQueue.ts
 * (LeadStatus / LEAD_STATUS_VALUES).
 */

export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'lost';

export const LEAD_STATUS_VALUES: LeadStatus[] = ['new', 'contacted', 'qualified', 'lost'];

/** 'manual' (set from Revcall) or 'crm' (received from a connected CRM). */
export type LeadStatusOrigin = 'manual' | 'crm';

type TranslateFn = (key: string) => string;

/** True when the raw value is one of the four canonical qualifications. */
export function isKnownLeadStatus(value: string | null | undefined): value is LeadStatus {
  return !!value && (LEAD_STATUS_VALUES as string[]).includes(value);
}

/**
 * leadStatusLabel — the translated label of a raw `lead_status` value.
 * Falls back to the raw value itself for anything outside the catalog
 * (legacy data, a value written before an enum change) rather than
 * hiding it.
 */
export function leadStatusLabel(value: string | null | undefined, t: TranslateFn): string {
  if (!value) return '';
  return isKnownLeadStatus(value) ? t(`dialer:leadStatus.${value}`) : value;
}
